import { FormulaOp, MetricFormat, MetricKind, type MetricDefinition } from "@gen/tracker/v1/reporting_pb";

import type { PickerGroupId } from "@/lib/column-picker";

/**
 * The campaign list's columns (board CampColumns) — the reporting schema's
 * metrics, as the server names, formats and describes them, plus «Исход
 * лидов», the one column that is a picture of four of them. Pure.
 *
 * Nothing here counts: a column is a key of the server's StatsRow, and a
 * workspace formula («Свои») is a metric of the schema like any other — the
 * picker only chooses among the formulas that exist (writing one is the
 * reports' formula editor, card У5).
 */

/** «Исход лидов»: апрув · холд · отказ · треш as shares of one bar (reporting.ColumnOutcome). */
export const OUTCOME = "outcome";
export const OUTCOME_PARTS = ["conversions", "conversions_hold", "rejected", "trash"] as const;

export type Band = "traffic" | "conversions" | "money" | "custom";
export const BANDS: readonly Band[] = ["traffic", "conversions", "money", "custom"];

export interface ListColumn {
  key: string;
  /** The schema's label; «Исход лидов» names itself in the caller's words. */
  label: string;
  /** The header's word when the schema has a shorter one. */
  short: string;
  description: string;
  band: Band;
  /** The picker's group: the band, with the goals' counts apart (lib/column-picker). */
  group: PickerGroupId;
  def?: MetricDefinition;
  /** A rate or a price per unit beside the counts it is made of: muted ink. */
  dim: boolean;
}

/** The built-in sets. «Мои» is the reader's own (the URL); a saved set is a view of surface «columns». */
export type PresetSet = "money" | "funnel" | "quality" | "compare";
export const PRESET_SETS: readonly PresetSet[] = ["money", "funnel", "quality", "compare"];
export const PRESET_COLUMNS: Record<PresetSet, readonly string[]> = {
  money: ["clicks", "leads", "cr", OUTCOME, "approve_rate", "cost", "revenue", "cpa", "profit", "roi"],
  funnel: ["visits", "clicks", "lander_shows", "cta_clicks", "lp_ctr", "leads", "conversions", "cr", "approve_rate"],
  quality: ["visits", "clicks", "bots", "bot_share", "unique_clicks", "lp_ctr", "leads", "cr", "approve_rate"],
  compare: ["clicks", "leads", "cr", "cost", "revenue", "profit", "roi"],
};
/**
 * «Деньги» on a wide screen (board Camp1920): the same money with the
 * traffic's quality beside it — more columns, not more emptiness.
 */
export const MONEY_WIDE: readonly string[] = [
  "visits",
  "clicks",
  "bot_share",
  "lp_ctr",
  "leads",
  "cr",
  OUTCOME,
  "conversions_hold",
  "approve_rate",
  "cost",
  "revenue",
  "epc",
  "cpa",
  "profit",
  "roi",
];

/**
 * «Деньги» on a 13" laptop (board Camp1280, the rail at 56): the money that
 * decides — clicks, leads, CR, what became of them, spend, profit, ROI —
 * without the revenue and the CPA a wider screen has room for, so no header
 * is cut and ROI is not pushed off the right edge.
 */
export const MONEY_NARROW: readonly string[] = ["clicks", "leads", "cr", OUTCOME, "approve_rate", "cost", "profit", "roi"];

/** How much room the list has: ≤1440px, the MacBook's 1512, ≥1800px (boards Camp1280, CampList, Camp1920). */
export type ListWidth = "narrow" | "base" | "wide";

/** Metrics a campaign row never has a value for: they are another screen's. */
const NOT_A_CAMPAIGN_COLUMN = new Set(["revenue_missing_fx", "decided", "lander_cr", "lander_epc", "offer_visits"]);
/** Rates that are the point of the column, not a footnote to it. */
const LOUD_RATES = new Set(["approve_rate", "roi", "roas"]);

function bandOf(m: MetricDefinition): { band: Band; group: PickerGroupId } {
  switch (m.category) {
    case "traffic":
      return { band: "traffic", group: "traffic" };
    case "conversions":
      return { band: "conversions", group: "conversions" };
    case "goals":
      return { band: "conversions", group: "goals" };
    case "custom":
      return { band: "custom", group: "custom" };
    default:
      return { band: "money", group: "money" };
  }
}

export function isRatio(m: MetricDefinition): boolean {
  return m.kind === MetricKind.DERIVED && m.formula?.op === FormulaOp.RATIO;
}

export function isPercentMetric(m: MetricDefinition | undefined): boolean {
  return m?.format === MetricFormat.PERCENT;
}

/** Every column the picker offers, in the schema's order, «Исход лидов» after the leads. Pure. */
export function columnCatalog(metrics: readonly MetricDefinition[], outcomeLabel: string): ListColumn[] {
  const out: ListColumn[] = [];
  const sorted = [...metrics].filter((m) => !NOT_A_CAMPAIGN_COLUMN.has(m.key)).sort((a, b) => a.sortOrder - b.sortOrder);
  for (const m of sorted) {
    const { band, group } = bandOf(m);
    out.push({
      key: m.key,
      label: m.label,
      short: m.shortLabel || m.label,
      description: m.description,
      band,
      group,
      def: m,
      dim: isRatio(m) && !LOUD_RATES.has(m.key),
    });
  }
  if (OUTCOME_PARTS.every((k) => metrics.some((m) => m.key === k))) {
    const at = out.findIndex((c) => c.key === "leads");
    out.splice(at < 0 ? out.length : at + 1, 0, {
      key: OUTCOME,
      label: outcomeLabel,
      short: outcomeLabel,
      description: "",
      band: "conversions",
      group: "conversions",
      dim: false,
    });
  }
  return out;
}

/**
 * The columns on screen: the keys the catalog has, grouped into the bands
 * (Трафик · Конверсии · Деньги · Свои) — a band is one header over adjacent
 * columns — each band keeping the reader's order. Pure.
 */
export function visibleColumns(keys: readonly string[], catalog: readonly ListColumn[]): ListColumn[] {
  const byKey = new Map(catalog.map((c) => [c.key, c]));
  const picked = [...new Set(keys)].flatMap((k) => {
    const c = byKey.get(k);
    return c ? [c] : [];
  });
  return BANDS.flatMap((band) => picked.filter((c) => c.band === band));
}

/** A set id of the URL: a preset, «mine» (the URL's own columns) or `v:<view id>` (a saved set). */
export type SetId = PresetSet | "mine" | `v:${string}`;

export function parseSet(raw: string | null | undefined): SetId {
  if (!raw) return "money";
  if ((PRESET_SETS as readonly string[]).includes(raw) || raw === "mine") return raw as SetId;
  if (raw.startsWith("v:") && raw.length > 2) return raw as SetId;
  return "money";
}

/**
 * The keys a set shows. «Мои» is the URL's list (else «Деньги»); a saved set
 * is its view's keys (else «Деньги» while it loads or after it was deleted);
 * «Деньги» widens on a wide screen and narrows on a narrow one. Pure.
 */
export function setColumns(
  set: SetId,
  opts: { mine: readonly string[]; saved: ReadonlyMap<string, readonly string[]>; width: ListWidth },
): readonly string[] {
  if (set === "mine") return opts.mine.length > 0 ? opts.mine : PRESET_COLUMNS.money;
  if (set.startsWith("v:")) return opts.saved.get(set.slice(2)) ?? PRESET_COLUMNS.money;
  if (set === "money" && opts.width === "wide") return MONEY_WIDE;
  if (set === "money" && opts.width === "narrow") return MONEY_NARROW;
  return PRESET_COLUMNS[set as PresetSet];
}
